import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Rating,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  AccessTime as AccessTimeIcon,
  Flag as FlagIcon,
  CalendarToday as CalendarTodayIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useAuth } from '../../contexts/AuthContext';
import { Task } from '../../types/task';
import TaskActions from '../../components/TaskActions';
import TaskChat from '../../components/TaskChat';
import ReportDialog from '../../components/ReportDialog';
import { translateCategory } from '../../utils/categoryTranslations';
import { translateTag } from '../../utils/tagTranslations';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../store/store';
import { fetchTaskDetails, selectCurrentTask, selectTasksLoading, selectTasksError } from '../../store/slices/tasksSlice';
import ImageGallery from '../../components/ImageGallery';
import FileUpload, { UploadedFile } from '../../components/FileUpload';
import { fileApi } from '../../services/api';
import { reviewService } from '../../services/reviewService';
import { Review as ReviewType } from '../../types/review';
import { bidService } from '../../services/bidService';
import { taskService } from '../../services/taskService';
import { formatCurrency } from '../../utils/currency';
import PageBackButton from '../../components/PageBackButton';

interface TaskBidCandidate {
  id: number;
  userId: number;
  amount: number;
  currency?: string;
  status: string;
}

const TaskDetails: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const currentTask = useSelector(selectCurrentTask);
  const loading = useSelector(selectTasksLoading);
  const error = useSelector(selectTasksError);
  const [taskFiles, setTaskFiles] = useState<UploadedFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [myReview, setMyReview] = useState<ReviewType | null>(null);
  const [reviewRating, setReviewRating] = useState<number | null>(5);
  const [reviewComment, setReviewComment] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewSuccess, setReviewSuccess] = useState<string | null>(null);
  const [reviewEditMode, setReviewEditMode] = useState(false);
  const [taskBidCandidates, setTaskBidCandidates] = useState<TaskBidCandidate[]>([]);
  const [selectedBidderId, setSelectedBidderId] = useState<number | null>(null);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [hasOwnBidForTask, setHasOwnBidForTask] = useState(false);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);
  const [favoriteSubmitting, setFavoriteSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchTaskDetails(parseInt(id)));
      loadTaskFiles(parseInt(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    const loadMyReview = async () => {
      if (!currentTask || !user) return;

      const canLeaveReview =
        user.id === currentTask.user_id &&
        currentTask.status === 'completed' &&
        !!currentTask.contractor_id;

      if (!canLeaveReview) {
        setMyReview(null);
        return;
      }

      try {
        setReviewLoading(true);
        const review = await reviewService.getMyTaskReview(currentTask.id);
        setMyReview(review);
        setReviewRating(review.rating);
        setReviewComment(review.comment);
        setReviewEditMode(false);
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          setReviewError(t('profile.save_error', 'Грешка при зареждане на отзива'));
        }
        setMyReview(null);
      } finally {
        setReviewLoading(false);
      }
    };

    loadMyReview();
  }, [currentTask, user, t]);

  useEffect(() => {
    const loadChatCandidates = async () => {
      if (!currentTask || !user) return;

      const currentUserId = Number(user.id);
      const ownerUserId = Number(currentTask.user_id);
      const contractorUserId = currentTask.contractor_id ? Number(currentTask.contractor_id) : null;

      // При вече избран изпълнител pre-bid селекцията не е нужна.
      if (contractorUserId !== null) {
        setTaskBidCandidates([]);
        setSelectedBidderId(null);
        setHasOwnBidForTask(false);
        return;
      }

      try {
        setBidsLoading(true);

        if (currentUserId === ownerUserId) {
          const rawBids = await bidService.getBidsByTaskId(currentTask.id);
          const candidates: TaskBidCandidate[] = (rawBids || [])
            .map((bid: any) => ({
              id: Number(bid.id),
              userId: Number(bid.user_id ?? bid.professional_id),
              amount: Number(bid.amount),
              currency: typeof bid.currency === 'string' ? bid.currency : 'EUR',
              status: String(bid.status || 'pending'),
            }))
            .filter((bid) => Number.isFinite(bid.userId) && bid.userId > 0 && bid.status !== 'rejected');

          setTaskBidCandidates(candidates);
          setHasOwnBidForTask(false);
          setSelectedBidderId((prev) => {
            if (prev && candidates.some((candidate) => candidate.userId === prev)) {
              return prev;
            }
            return candidates[0]?.userId ?? null;
          });
        } else {
          const myBids = await bidService.getMyBids();
          const hasBid = (myBids || []).some((bid: any) => {
            const sameTask = Number(bid.task_id) === Number(currentTask.id);
            const status = String(bid.status || '');
            return sameTask && status !== 'rejected';
          });

          setHasOwnBidForTask(hasBid);
          setTaskBidCandidates([]);
          setSelectedBidderId(null);
        }
      } catch (err) {
        console.error('Error loading bid chat candidates:', err);
        setTaskBidCandidates([]);
        setSelectedBidderId(null);
        setHasOwnBidForTask(false);
      } finally {
        setBidsLoading(false);
      }
    };

    loadChatCandidates();
  }, [currentTask, user]);

  const loadTaskFiles = async (taskId: number) => {
    try {
      setFilesLoading(true);
      setFilesError(null);
      const files = await fileApi.getTaskFiles(taskId);
      setTaskFiles(files);
    } catch (err) {
      console.error('Error loading task files:', err);
      setFilesError(t('common.error_try_again'));
    } finally {
      setFilesLoading(false);
    }
  };

  const handleFileUpload = (files: UploadedFile[]) => {
    setTaskFiles((prev) => [...prev, ...files]);
  };

  const handleFileDelete = async (fileId: number) => {
    try {
      await fileApi.deleteFile(fileId);
      // Премахваме файла от списъка
      setTaskFiles((prev) => prev.filter((file) => file.id !== fileId));
    } catch (err) {
      console.error('Error deleting file:', err);
      throw err; // Хвърляме грешката за да покажем в UI
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'open':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'primary';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: Task['status']) => {
    return t(`tasks.list.filter.${status}`);
  };

  const getLocale = () => getDateFnsLocale(i18n.language);

  const formatDate = (dateString: string) => {
    try {
      if (!dateString) return t('common.unknown_date');
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date: ${dateString}`);
        return t('common.unknown_date');
      }
      return format(date, 'PPP', { locale: getLocale() });
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('common.unknown_date');
    }
  };

  const canLeaveReview =
    !!user &&
    user.id === currentTask?.user_id &&
    currentTask?.status === 'completed' &&
    !!currentTask?.contractor_id;

  const handleSubmitReview = async () => {
    if (!currentTask) return;

    if (!reviewRating || reviewRating < 1 || reviewRating > 5) {
      setReviewError(t('common.error', 'Грешка'));
      return;
    }

    const trimmedComment = reviewComment.trim();
    if (trimmedComment.length < 5) {
      setReviewError(t('tasks.details.description', 'Описание') + ': минимум 5 символа');
      return;
    }

    try {
      setReviewSubmitting(true);
      setReviewError(null);
      setReviewSuccess(null);

      const created = await reviewService.createReview({
        task_id: currentTask.id,
        rating: reviewRating,
        comment: trimmedComment,
      });

      setMyReview(created);
      setReviewSuccess('Отзивът е записан успешно.');
    } catch (err: any) {
      setReviewError(err?.response?.data?.detail || 'Неуспешно записване на отзив.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const isReviewWithinEditWindow = (review: ReviewType | null) => {
    if (!review?.created_at) return false;
    const createdAtMs = new Date(review.created_at).getTime();
    if (Number.isNaN(createdAtMs)) return false;
    const elapsedMs = Date.now() - createdAtMs;
    return elapsedMs <= 24 * 60 * 60 * 1000;
  };

  const handleUpdateReview = async () => {
    if (!myReview || !reviewRating) return;

    const trimmedComment = reviewComment.trim();
    if (trimmedComment.length < 5) {
      setReviewError(t('tasks.details.description', 'Описание') + ': минимум 5 символа');
      return;
    }

    try {
      setReviewSubmitting(true);
      setReviewError(null);
      setReviewSuccess(null);
      const updated = await reviewService.updateReview(myReview.id, {
        rating: reviewRating,
        comment: trimmedComment,
      });
      setMyReview(updated);
      setReviewEditMode(false);
      setReviewSuccess('Отзивът е обновен успешно.');
    } catch (err: any) {
      setReviewError(err?.response?.data?.detail || 'Неуспешно обновяване на отзив.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const handleDeleteReview = async () => {
    if (!myReview) return;
    if (!window.confirm('Сигурни ли сте, че искате да изтриете отзива?')) return;

    try {
      setReviewSubmitting(true);
      setReviewError(null);
      setReviewSuccess(null);
      await reviewService.deleteReview(myReview.id);
      setMyReview(null);
      setReviewRating(5);
      setReviewComment('');
      setReviewEditMode(false);
      setReviewSuccess('Отзивът е изтрит успешно.');
    } catch (err: any) {
      setReviewError(err?.response?.data?.detail || 'Неуспешно изтриване на отзив.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-8 text-center">
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <PageBackButton />
        </Box>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !currentTask) {
    return (
      <Container className="py-8">
        <PageBackButton />
        <Alert severity="error">{error || t('common.error')}</Alert>
      </Container>
    );
  }

  const currentUserId = user?.id ? Number(user.id) : null;
  const ownerUserId = Number(currentTask.user_id);
  const contractorUserId = currentTask.contractor_id ? Number(currentTask.contractor_id) : null;
  const isOwner = currentUserId !== null && currentUserId === ownerUserId;
  const isContractor = currentUserId !== null && contractorUserId !== null && currentUserId === contractorUserId;
  const isParticipant = isOwner || isContractor;

  let chatOtherUserId: number | null = null;
  if (isOwner) {
    // Приета оферта => пишем с contractor; иначе клиентът избира оферент за pre-bid chat.
    chatOtherUserId = contractorUserId ?? selectedBidderId;
  } else if (isContractor) {
    chatOtherUserId = ownerUserId;
  } else if (hasOwnBidForTask) {
    // Професионалист с подадена оферта може да пише с клиента преди приемане.
    chatOtherUserId = ownerUserId;
  }

  const showBidderSelector = isOwner && contractorUserId === null && taskBidCandidates.length > 0;
  const canEditMyReview = isReviewWithinEditWindow(myReview);
  const favoriteEnabled = !!user && currentUserId !== ownerUserId;
  const isFavorited = Boolean(currentTask.metrics?.is_favorited);
  const showMetricsCounts =
    currentTask.metrics?.professional_views !== null &&
    currentTask.metrics?.professional_views !== undefined &&
    currentTask.metrics?.professional_favorites !== null &&
    currentTask.metrics?.professional_favorites !== undefined;

  const handleToggleFavorite = async () => {
    if (!id || !favoriteEnabled) return;
    try {
      setFavoriteSubmitting(true);
      await taskService.toggleTaskFavorite(Number(id));
      dispatch(fetchTaskDetails(Number(id)));
    } catch (err) {
      console.error('Failed to toggle task favorite:', err);
    } finally {
      setFavoriteSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageBackButton />

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h4" component="h1">
            {currentTask.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={getStatusText(currentTask.status)}
              color={getStatusColor(currentTask.status)}
            />
            {user && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => setIsReportDialogOpen(true)}
              >
                {t('admin.reports.report_task_button')}
              </Button>
            )}
          </Box>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('tasks.details.description')}
            </Typography>
            <Typography sx={{ mb: 3, whiteSpace: 'pre-line' }}>
              {currentTask.description}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountBalanceIcon sx={{ mr: 1 }} color="action" />
                  <Typography>
                    {t('tasks.list.card.budget')}: {formatCurrency(currentTask.budget, i18n.language, currentTask.currency)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccessTimeIcon sx={{ mr: 1 }} color="action" />
                  <Typography>
                    {t('tasks.list.card.deadline')}: {formatDate(currentTask.deadline)}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FlagIcon sx={{ mr: 1 }} color="action" />
                  <Typography>{currentTask.address}</Typography>
                </Box>
              </Grid>
              
              {currentTask.category && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarTodayIcon sx={{ mr: 1 }} color="action" />
                    <Typography>
                      {t('tasks.details.category')}: {translateCategory(currentTask.category.name, i18n.language)}
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              {currentTask.tags && currentTask.tags.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ mr: 1 }}>
                      {t('tasks.details.tags')}:
                    </Typography>
                    {currentTask.tags.map(tag => (
                      <Chip 
                        key={tag.id} 
                        label={translateTag(tag.name, i18n.language)} 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                        sx={{ mr: 0.75, mb: 0.75 }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>

            {currentTask.status === 'open' && user?.user_type === 'professional' && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate(`/bids/create/${currentTask.id}`)}
                fullWidth
              >
                {t('tasks.place_bid')}
              </Button>
            )}

            {favoriteEnabled && (
              <Button
                variant={isFavorited ? 'contained' : 'outlined'}
                color={isFavorited ? 'secondary' : 'primary'}
                onClick={handleToggleFavorite}
                startIcon={isFavorited ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                sx={{ mt: 2 }}
                disabled={favoriteSubmitting}
              >
                {isFavorited
                  ? t('tasks.details.remove_from_favorites')
                  : t('tasks.details.add_to_favorites')}
              </Button>
            )}

            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('tasks.details.metrics_title')}
              </Typography>
              {showMetricsCounts ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="body2">
                    <VisibilityIcon fontSize="inherit" sx={{ mr: 0.75, verticalAlign: 'middle' }} />
                    {t('tasks.details.professional_views')}: {currentTask.metrics?.professional_views}
                  </Typography>
                  <Typography variant="body2">
                    <FavoriteIcon fontSize="inherit" sx={{ mr: 0.75, verticalAlign: 'middle' }} />
                    {t('tasks.details.professional_favorites')}: {currentTask.metrics?.professional_favorites}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('tasks.details.metrics_hidden')}
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('tasks.details.client_info')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ mr: 2 }}>
                  {currentTask.client?.fullName?.[0] || currentTask.client?.email?.[0] || '?'}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">
                    {currentTask.client?.fullName || currentTask.client?.email || t('common.unknown_user')}
                  </Typography>
                  {currentTask.client?.email && (
                    <Typography variant="body2" color="text.secondary">
                      {currentTask.client.email}
                    </Typography>
                  )}
                </Box>
              </Box>

              {currentTask.client?.phone && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('common.phone')}: {currentTask.client.phone}
                </Typography>
              )}

              {currentTask.client?.location && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('common.location')}: {currentTask.client.location}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <TaskActions 
        task={currentTask} 
        onTaskUpdated={() => {
          if (id) {
            dispatch(fetchTaskDetails(parseInt(id)));
          }
        }} 
      />

      {canLeaveReview && (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {myReview ? 'Вашият отзив' : 'Остави отзив'}
          </Typography>

          {reviewLoading && <CircularProgress size={24} />}

          {!reviewLoading && myReview && (
            <Box>
              <Rating
                value={reviewEditMode ? reviewRating : myReview.rating}
                precision={0.5}
                readOnly={!reviewEditMode}
                onChange={(_, value) => {
                  if (reviewEditMode) {
                    setReviewRating(value);
                  }
                }}
              />
              {reviewEditMode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  sx={{ mt: 2 }}
                  label="Коментар"
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                />
              ) : (
                <Typography sx={{ mt: 1, whiteSpace: 'pre-line' }}>{myReview.comment}</Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formatDate(myReview.created_at)}
              </Typography>
              {canEditMyReview ? (
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                  {reviewEditMode ? (
                    <>
                      <Button variant="contained" onClick={handleUpdateReview} disabled={reviewSubmitting}>
                        {reviewSubmitting ? 'Запазване...' : 'Запази промените'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setReviewEditMode(false);
                          setReviewRating(myReview.rating);
                          setReviewComment(myReview.comment);
                        }}
                        disabled={reviewSubmitting}
                      >
                        {t('common.cancel', 'Отказ')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outlined" onClick={() => setReviewEditMode(true)}>
                        {t('common.edit', 'Редактирай')}
                      </Button>
                      <Button color="error" variant="outlined" onClick={handleDeleteReview} disabled={reviewSubmitting}>
                        {t('common.delete', 'Изтрий')}
                      </Button>
                    </>
                  )}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Редакция/изтриване е достъпно до 24 часа след публикуване.
                </Typography>
              )}
            </Box>
          )}

          {!reviewLoading && !myReview && (
            <Box>
              <Rating
                value={reviewRating}
                onChange={(_, value) => setReviewRating(value)}
                precision={1}
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                sx={{ mt: 2 }}
                label="Коментар"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
              />
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={handleSubmitReview}
                disabled={reviewSubmitting}
              >
                {reviewSubmitting ? 'Изпращане...' : 'Изпрати отзив'}
              </Button>
            </Box>
          )}

          {reviewError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {reviewError}
            </Alert>
          )}

          {reviewSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {reviewSuccess}
            </Alert>
          )}
        </Paper>
      )}

      {/* Unified attachments section (gallery + upload) */}
      {(!filesLoading && taskFiles.length > 0) || isParticipant ? (
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('tasks.details.attachments', 'Прикачени файлове')}
          </Typography>

          {!filesLoading && taskFiles.length > 0 ? (
            <ImageGallery
              files={taskFiles}
              onDelete={handleFileDelete}
              currentUserId={user?.id}
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('common.no_data', 'Няма налични данни')}
            </Typography>
          )}

          {isParticipant && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('tasks.details.add_attachments', 'Добави файлове')}
              </Typography>
              <FileUpload
                taskId={currentTask.id}
                multiple={true}
                onUploadComplete={handleFileUpload}
              />
            </Box>
          )}
        </Paper>
      ) : null}

      {filesError && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          {filesError}
        </Alert>
      )}

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('tasks.details.chat_section')}
        </Typography>

        {showBidderSelector && (
          <FormControl fullWidth sx={{ mb: 2 }} data-testid="prebid-chat-bidder-selector">
            <InputLabel id="chat-bidder-select-label">
              {t('tasks.details.chat_with', 'Чат с')}
            </InputLabel>
            <Select
              labelId="chat-bidder-select-label"
              value={selectedBidderId ?? ''}
              label={t('tasks.details.chat_with', 'Чат с')}
              onChange={(event) => {
                const value = Number(event.target.value);
                setSelectedBidderId(Number.isFinite(value) ? value : null);
              }}
            >
              {taskBidCandidates.map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.userId}>
                  {`#${candidate.id} • ${formatCurrency(candidate.amount, i18n.language, candidate.currency)}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {bidsLoading && isOwner && contractorUserId === null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t('common.loading', 'Зареждане...')}
            </Typography>
          </Box>
        )}

        {typeof chatOtherUserId === 'number' ? (
          <TaskChat 
            taskId={currentTask.id.toString()}
            otherUserId={chatOtherUserId}
          />
        ) : (
          <Alert severity="info" data-testid="task-chat-unavailable-alert">
            {t('tasks.details.chat_not_available')}
          </Alert>
        )}
      </Paper>

      {user && (
        <ReportDialog
          open={isReportDialogOpen}
          onClose={() => setIsReportDialogOpen(false)}
          targetType="task"
          targetId={currentTask.id}
          targetLabel={t('admin.reports.target_label_task', { id: currentTask.id })}
        />
      )}
    </Container>
  );
};

export default TaskDetails; 